import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { Loader, AlertCircle, Save } from 'lucide-react';

const OwnersPage = () => {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ show: false, message: '', type: '' });
  const [pendingChanges, setPendingChanges] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    loadOwners();
  }, []);

  const loadOwners = async () => {
    try {
      const ownersCollection = collection(db, 'owners');
      const ownersSnapshot = await getDocs(ownersCollection);
      const ownersData = ownersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOwners(ownersData);
    } catch (err) {
      setError('אירעה שגיאה בטעינת הנתונים');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file, ownerId) => {
    try {
      const storageRef = ref(storage, `owners/${ownerId}/${file.name}`);
      await uploadBytes(storageRef, file);
      const imageUrl = await getDownloadURL(storageRef);
      setPendingChanges(prev => ({
        ...prev,
        [ownerId]: {
          ...prev[ownerId],
          image: imageUrl,
          imageFile: file
        }
      }));
    } catch (err) {
      throw new Error('שגיאה בהעלאת התמונה');
    }
  };

  const handleInputChange = (ownerId, field, value) => {
    setPendingChanges(prev => ({
      ...prev,
      [ownerId]: {
        ...prev[ownerId],
        [field]: value
      }
    }));
  };

  const handleSave = async (ownerId) => {
    if (!pendingChanges[ownerId]) return;

    setSavingId(ownerId);
    setSaveStatus({ show: true, message: 'שומר שינויים...', type: 'info' });
    
    try {
      const ownerRef = doc(db, 'owners', ownerId);
      await updateDoc(ownerRef, pendingChanges[ownerId]);
      
      setOwners(owners.map(owner => 
        owner.id === ownerId ? { ...owner, ...pendingChanges[ownerId] } : owner
      ));

      // נקה את השינויים הממתינים לאחר שמירה מוצלחת
      setPendingChanges(prev => {
        const newChanges = { ...prev };
        delete newChanges[ownerId];
        return newChanges;
      });

      setSaveStatus({ show: true, message: 'נשמר בהצלחה!', type: 'success' });
      setTimeout(() => setSaveStatus({ show: false, message: '', type: '' }), 3000);
    } catch (err) {
      setSaveStatus({ show: true, message: 'שגיאה בשמירת הנתונים', type: 'error' });
      console.error(err);
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-red-500">
        <AlertCircle className="w-6 h-6 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl md:text-2xl font-bold">עריכת בעלי המשרד</h1>
        {saveStatus.show && (
          <div className={`
            px-4 py-2 rounded-lg text-sm md:text-base
            ${saveStatus.type === 'success' ? 'bg-green-100 text-green-700' : ''}
            ${saveStatus.type === 'error' ? 'bg-red-100 text-red-700' : ''}
            ${saveStatus.type === 'info' ? 'bg-blue-100 text-blue-700' : ''}
          `}>
            {saveStatus.message}
          </div>
        )}
      </div>

      <div className="space-y-6">
        {owners.map(owner => (
          <div key={owner.id} className="bg-white p-4 md:p-6 rounded-lg shadow-md">
            <div className="flex flex-col md:flex-row gap-4 md:gap-6">
              <div className="w-full md:w-32">
                <img 
                  src={pendingChanges[owner.id]?.image || owner.image || '/placeholder.jpg'} 
                  alt={owner.ownerName}
                  className="w-full h-48 md:h-32 object-cover rounded-lg"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files[0], owner.id)}
                  className="mt-2 text-sm w-full"
                />
              </div>
              
              <div className="flex-1 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    שם
                  </label>
                  <input
                    type="text"
                    value={pendingChanges[owner.id]?.ownerName ?? owner.ownerName}
                    onChange={(e) => handleInputChange(owner.id, 'ownerName', e.target.value)}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    תיאור
                  </label>
                  <textarea
                    value={pendingChanges[owner.id]?.description ?? owner.description}
                    onChange={(e) => handleInputChange(owner.id, 'description', e.target.value)}
                    rows="4"
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                {pendingChanges[owner.id] && (
                  <button
                    onClick={() => handleSave(owner.id)}
                    disabled={savingId === owner.id}
                    className={`
                      w-full md:w-auto mt-4 px-6 py-2 rounded-md
                      flex items-center justify-center space-x-2 space-x-reverse
                      ${savingId === owner.id
                        ? 'bg-blue-300 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                      }
                      text-white font-medium transition-colors
                    `}
                  >
                    <Save className="w-4 h-4" />
                    <span>{savingId === owner.id ? 'שומר...' : 'שמור שינויים'}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OwnersPage;